/**
 * Moldura "Só o selo" em PNG (1080×1350), desenhada no navegador: porta do `desenhar()` do
 * `selo-moldura.js` do Claude Design (AJ2-35). A moldura "Com sua foto" continua vindo dos triggers.
 *
 * SPEC_DEVIATION: o design.md põe este módulo em `src/pages/Selos/`.
 * Reason: o canvas precisa das cores em hex, e o gate SEL-61 (`sem-cores-legadas.spec.ts`) proíbe cor
 * literal nos fontes da página de selos; o desenho fica em `utils`, como dado de imagem e não estilo da página.
 */
import logoJogaJunto from 'assets/img/logo-jj.png';

/** O que a moldura mostra de um selo (`SeloDaColecao` serve). */
export interface SeloDaMoldura {
  nome: string;
  categoria: string;
  ciclo: string;
  /** Arte do selo; sem ela não há moldura (AJ2-37). */
  arte?: string;
}

export const LARGURA_DA_MOLDURA = 1080;
export const ALTURA_DA_MOLDURA = 1350;

const AZUL = '#465EFF';
const AMARELO = '#FCFC30';
const LILAS = '#BDB6FF';
const AZUL_DO_ANEL_EXTERNO = '#3C48DE';
const AZUL_DO_ANEL_INTERNO = '#5168FF';
const AZUL_DO_NOME = '#3333BD';
const CINZA_DO_CICLO = '#3D4A5C';

/** Nomes longos quebram em linhas que caibam entre as margens. */
const LARGURA_UTIL_DO_NOME = LARGURA_DA_MOLDURA - 200;
const ALTURA_DA_LINHA_DO_NOME = 74;
const TAMANHO_DA_ARTE = 420;
/** Centro do selo e dos anéis. */
const CENTRO_DO_SELO = 520;

const TITULOS = (peso: number, tamanho: number) => `${peso} ${tamanho}px "BancoDoBrasil Titulos", system-ui, sans-serif`;
const TEXTOS = (peso: number, tamanho: number) => `${peso} ${tamanho}px "BancoDoBrasil Textos", system-ui, sans-serif`;

type Imagem = CanvasImageSource & { width: number; height: number };

/**
 * "Realização Destaque" → "realizacao-destaque".
 * SPEC_DEVIATION: o `slug` do `selo-moldura.js` troca o acento decomposto por hífen ("realizac-a-o").
 * Reason: aqui o acento é removido antes, para o nome do arquivo ficar legível.
 */
function slug(texto: string) {
  return (
    texto
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'selo'
  );
}

/** AJ2-26: `selo-{slug(nome)}-{slug(ciclo)}.png`. */
export function arquivoDoSoOSelo(selo: SeloDaMoldura) {
  return `selo-${slug(selo.nome)}-${slug(selo.ciclo)}.png`;
}

function carregarImagem(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.crossOrigin = 'anonymous';
    imagem.onload = () => resolve(imagem);
    imagem.onerror = () => reject(new Error(`Não foi possível carregar ${src}`));
    imagem.src = src;
  });
}

function circulo(c: CanvasRenderingContext2D, x: number, y: number, raio: number, cor: string) {
  c.fillStyle = cor;
  c.beginPath();
  c.arc(x, y, raio, 0, Math.PI * 2);
  c.fill();
}

function caixa(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath();
  if (c.roundRect) c.roundRect(x, y, w, h, r);
  else c.rect(x, y, w, h);
  c.fill();
}

/** Quebra o nome em linhas de até `LARGURA_UTIL_DO_NOME`, medidas com a fonte já aplicada. */
function linhasDoNome(c: CanvasRenderingContext2D, nome: string) {
  return nome.split(' ').reduce<string[]>((linhas, palavra) => {
    const atual = linhas[linhas.length - 1];
    if (atual === undefined) return [palavra];
    const junta = `${atual} ${palavra}`;
    return c.measureText(junta).width > LARGURA_UTIL_DO_NOME ? [...linhas, palavra] : [...linhas.slice(0, -1), junta];
  }, []);
}

function desenharFundo(c: CanvasRenderingContext2D, logo: Imagem | null) {
  const W = LARGURA_DA_MOLDURA;
  const cx = W / 2;

  c.fillStyle = AZUL;
  c.fillRect(0, 0, W, ALTURA_DA_MOLDURA);
  circulo(c, 80, 80, 22, AMARELO);
  circulo(c, 990, 230, 14, AMARELO);
  circulo(c, 190, 520, 11, LILAS);

  if (logo) {
    const larguraDoLogo = 260;
    const alturaDoLogo = logo.height * (larguraDoLogo / logo.width);
    c.drawImage(logo, cx - larguraDoLogo / 2, 120 - alturaDoLogo / 2, larguraDoLogo, alturaDoLogo);
  }

  circulo(c, cx, CENTRO_DO_SELO, 290, AZUL_DO_ANEL_EXTERNO);
  circulo(c, cx, CENTRO_DO_SELO, 250, AZUL_DO_ANEL_INTERNO);

  c.fillStyle = AMARELO;
  c.beginPath();
  c.moveTo(0, ALTURA_DA_MOLDURA);
  c.lineTo(0, 860);
  c.quadraticCurveTo(cx, 720, W, 860);
  c.lineTo(W, ALTURA_DA_MOLDURA);
  c.closePath();
  c.fill();
}

function desenharArte(c: CanvasRenderingContext2D, arte: Imagem) {
  const cx = LARGURA_DA_MOLDURA / 2;
  const escala = Math.min(TAMANHO_DA_ARTE / arte.width, TAMANHO_DA_ARTE / arte.height);
  const largura = arte.width * escala;
  const altura = arte.height * escala;

  c.save();
  c.shadowColor = 'rgba(0,0,0,0.25)';
  c.shadowBlur = 30;
  c.shadowOffsetY = 14;
  c.drawImage(arte, cx - largura / 2, CENTRO_DO_SELO - altura / 2, largura, altura);
  c.restore();
}

function desenharTextos(c: CanvasRenderingContext2D, selo: SeloDaMoldura) {
  const cx = LARGURA_DA_MOLDURA / 2;
  c.textAlign = 'center';

  c.fillStyle = AZUL;
  c.font = TITULOS(700, 32);
  const apoio = selo.categoria ? `${selo.categoria} · ${selo.ciclo}` : selo.ciclo;
  c.fillText(apoio.toUpperCase(), cx, 940);

  c.fillStyle = AZUL_DO_NOME;
  c.font = TITULOS(800, 66);
  let y = 1024;
  linhasDoNome(c, selo.nome).forEach((linha, indice) => {
    y = 1024 + indice * ALTURA_DA_LINHA_DO_NOME;
    c.fillText(linha, cx, y);
  });

  // AJ2-35: a coleção não traz a data da conquista; no lugar de "Conquistado em …", o ciclo.
  c.fillStyle = CINZA_DO_CICLO;
  c.font = TEXTOS(400, 30);
  c.fillText(`Ciclo ${selo.ciclo}`, cx, y + 58);

  c.fillStyle = AZUL;
  caixa(c, cx - 50, 1200, 100, 5, 3);
  c.font = TITULOS(800, 58);
  c.fillText('#JogaJunto', cx, 1282);
}

function paraPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Canvas sem imagem'))), 'image/png');
  });
}

// Uma moldura por selo na sessão: trocar de modo ou de filtro não redesenha nem rebaixa a arte (AJ2-26).
const molduras = new Map<string, Promise<Blob>>();

function chaveDaMoldura(selo: SeloDaMoldura) {
  return [selo.arte, selo.nome, selo.categoria, selo.ciclo].join('|');
}

/**
 * Desenha a moldura "Só o selo", uma vez por selo: as chamadas seguintes reaproveitam o desenho. Uma falha
 * não fica guardada, para a próxima tentativa desenhar de novo. Espera as fontes da identidade (AJ2-36) e rejeita quando a arte
 * do selo falta ou não carrega (AJ2-37); sem o logo, a moldura sai sem ele, como no design.
 */
export function desenharSoOSelo(selo: SeloDaMoldura): Promise<Blob> {
  const chave = chaveDaMoldura(selo);
  const guardada = molduras.get(chave);
  if (guardada) return guardada;

  const desenho = desenhar(selo);
  molduras.set(chave, desenho);
  desenho.catch(() => molduras.delete(chave));
  return desenho;
}

/** Para os testes: esquece as molduras já desenhadas. */
export function esquecerMolduras() {
  molduras.clear();
}

async function desenhar(selo: SeloDaMoldura): Promise<Blob> {
  await document.fonts?.ready.catch(() => undefined);
  if (!selo.arte) throw new Error('Selo sem arte');

  const [arte, logo] = await Promise.all([carregarImagem(selo.arte), carregarImagem(logoJogaJunto).catch(() => null)]);

  const canvas = document.createElement('canvas');
  canvas.width = LARGURA_DA_MOLDURA;
  canvas.height = ALTURA_DA_MOLDURA;
  const contexto = canvas.getContext('2d');
  if (!contexto) throw new Error('Canvas indisponível');

  desenharFundo(contexto, logo);
  desenharArte(contexto, arte);
  desenharTextos(contexto, selo);

  return paraPng(canvas);
}
